import React, { useState } from "react";
import { customerCreate, customerLogin, fetchCustomer } from "./ShopifyClient";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (customer: any) => void;
}

export default function AuthModal({ isOpen, onClose, onAuthSuccess }: AuthModalProps) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isSignUp) {
        // Sign up
        const result = await customerCreate(
          formData.email,
          formData.password,
          formData.firstName,
          formData.lastName
        );

        if (result?.customerUserErrors && result.customerUserErrors.length > 0) {
          setError(result.customerUserErrors[0].message);
          setLoading(false);
          return;
        }

        // After successful signup, log them in
        const loginResult = await customerLogin(formData.email, formData.password);
        
        if (loginResult?.customerUserErrors && loginResult.customerUserErrors.length > 0) {
          setError(loginResult.customerUserErrors[0].message);
          setLoading(false);
          return;
        }

        if (loginResult?.customerAccessToken) {
          localStorage.setItem("customer_token", loginResult.customerAccessToken.accessToken);
          const customer = await fetchCustomer(loginResult.customerAccessToken.accessToken);
          onAuthSuccess(customer);
          onClose();
        }
      } else {
        // Sign in
        const result = await customerLogin(formData.email, formData.password);
        
        if (result?.customerUserErrors && result.customerUserErrors.length > 0) {
          setError(result.customerUserErrors[0].message);
          setLoading(false);
          return;
        }

        if (result?.customerAccessToken) {
          localStorage.setItem("customer_token", result.customerAccessToken.accessToken);
          const customer = await fetchCustomer(result.customerAccessToken.accessToken);
          onAuthSuccess(customer);
          onClose();
        }
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    }
    
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({ email: "", password: "", firstName: "", lastName: "" });
    setError("");
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(5px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1050
    }}>
      <div className="modal-dialog" style={{ maxWidth: "500px", width: "90%" }}>
        <div className="modal-content" style={{
          backgroundColor: "white",
          color: "black",
          border: "1px solid #dee2e6",
          borderRadius: "0.375rem",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
          padding: "1.5rem"
        }}>
          <div className="modal-header">
            <h1 className="modal-title">
              {isSignUp ? "Create Account" : "Sign In"}
            </h1>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "1.5rem",
                cursor: "pointer"
              }}
            >
              &times;
            </button>
          </div>
          <div className="modal-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {isSignUp && (
                <>
                  <div className="mb-3">
                    <label htmlFor="firstName" className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="lastName" className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </>
              )}
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn w-100"
                disabled={loading}
                style={{
                  backgroundColor: "#155d68",
                  borderColor: "#155d68",
                  color: "white"
                }}
              >
                {loading ? "Please wait..." : (isSignUp ? "Create Account" : "Sign In")}
              </button>
            </form>
          </div>
          <div className="modal-footer">
            <p className="text-center w-100 mb-0">
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={switchMode}
                style={{ textDecoration: "underline" }}
              >
                {isSignUp ? "Sign In" : "Sign Up"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
